import _map from "lodash/map"

import getGlobalSiteSettings from "@src/pages/api/get-global-site-settings"
import getJournalPaths from "@src/pages/api/get-journal-paths"

import apolloClient from "@src/utilities/apollo-client"
import convertStoryToJournalExcerpt from "@src/utilities/convert-story-to-journal-excerpt"
import { GET_NEXT_JOURNAL_EXCERPT } from "@src/utilities/graphql-storyblok-queries"
import { isCleanSite } from "@src/utilities/clean-site"
import Storyblok from "@src/utilities/storyblok"
import getStoryblokApiKey from "@src/utilities/storyblok-api-key"

import getStoryblokCacheVersion from "@src/workers/storyblok/get-cache-version"

import useStoryblokBridge from "@src/hooks/use-storyblok-bridge"

import GeneralLayout from "@src/layouts/general"
import JournalArticle from "@src/components/JournalArticle"
import SEO from "@src/components/seo"

import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import type {
  GetNextJournalExcerptQuery,
  GetNextJournalExcerptQueryVariables,
} from "@src/entities/types/storyblok"

export default function JournalArticlePage({
  nextStory,
  preview,
  siteUrl,
  siteSettings,
  story,
  storyblokApiKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const blok = useStoryblokBridge(story, storyblokApiKey, preview)

  return (
    <GeneralLayout siteSettings={siteSettings}>
      {blok.content && blok.content.seo && <SEO {...blok.content.seo} />}
      <JournalArticle blok={blok} nextArticle={nextStory} siteUrl={siteUrl} />
    </GeneralLayout>
  )
}

export async function getStaticPaths() {
  if (isCleanSite()) {
    return {
      paths: [],
      fallback: false,
    }
  }
  const paths = await getJournalPaths()
  return {
    paths: _map(paths, (slug) => ({
      params: {
        slug,
      },
    })),
    fallback: false,
  }
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const slug = (context.params && (context.params.slug as string)) ?? ""
  const apiKey = getStoryblokApiKey(context.preview)
  const cacheVersion = await getStoryblokCacheVersion()
  const [siteSettings, storyRequest] = await Promise.all([
    getGlobalSiteSettings(context.preview ?? false),
    Storyblok(apiKey).get(`cdn/stories/journal/${slug}`, {
      cv: cacheVersion,
      version: context.preview ? "draft" : "published",
    }),
  ])
  const nextStory = await apolloClient.query<
    GetNextJournalExcerptQuery,
    GetNextJournalExcerptQueryVariables
  >({
    context: {
      clientName: "storyblok",
      headers: {
        Token: apiKey,
        Version: cacheVersion,
      },
    },
    query: GET_NEXT_JOURNAL_EXCERPT,
    variables: {
      date:
        storyRequest.data.story.first_published_at ||
        storyRequest.data.story.created_at,
      slug: storyRequest.data.story.full_slug,
    },
  })

  return {
    notFound: isCleanSite(),
    props: {
      ...(nextStory?.data?.JournalpostItems?.items &&
        nextStory.data.JournalpostItems.items.length > 0 && {
          nextStory: convertStoryToJournalExcerpt({
            content: nextStory.data.JournalpostItems.items[0]?.content,
            fullSlug: nextStory.data.JournalpostItems.items[0]?.full_slug!,
          }),
        }),
      preview: context.preview ?? false,
      siteSettings,
      siteUrl: process.env.NEXT_PUBLIC_WEBSITE_BASE_URL!,
      story: storyRequest.data.story,
      ...(context.preview && { storyblokApiKey: apiKey }),
    },
    revalidate: 5 * 60, // In seconds
  }
}
